<template>
  <v-card elevation="0" class="bg-background pa-5 rounded-lg">
    <!-- Alertas -->
    <v-alert v-model="activeAlert" :type="colorAlert" dismissible class="mb-4">
      {{ textAlert }}
    </v-alert>

    <!-- Vista principal de ubicaciones -->
    <div v-if="!activeCreateNewLocation && !activeEditLocation">
      <v-card class="mt-3 rounded-lg" elevation="3">
        <div class="rounded-lg justify-space-between mb-4 pa-2 d-flex">
          <v-card-title color="primary" class="text-subtitle-4">Ubicaciones</v-card-title>
          <v-spacer />
          <v-btn
            v-if="!activeCreateNewLocation && !activeEditLocation"
            @click="btnSetActiveCreateLocation"
            variant="text"
            text="Nueva ubicación"
          />
          <v-btn
            v-else
            @click="(activeCreateNewLocation = false), (activeEditLocation = false)"
            variant="text"
            text="Volver a las ubicaciones"
          />
        </div>

        <v-text-field
          v-model="searchLocations"
          append-inner-icon="mdi-magnify"
          density="compact"
          label="Buscar ubicación"
          variant="solo"
          hide-details
          single-line
          color="surface-light"
          class="mx-4"
          bg-color="background"
        />

        <v-data-table
          class="rounded-lg text-body-2"
          v-model:page="page"
          :headers="headers"
          :items="locationsList"
          :search="searchLocations"
          :loading="loading"
          :items-per-page="itemsPerPage"
        >
          <template v-slot:loading>
            <v-skeleton-loader class="bg-background" type="table-row@10" />
          </template>

          <template #[`item.code`]="{ item }">
            <v-chip color="blue" variant="outlined" small>
              {{ item.code }}
            </v-chip>
          </template>

          <template #[`item.name`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="36" class="mr-3" color="primary">
                <span class="text-white text-subtitle-2 font-weight-bold">
                  {{ item.code?.substring(0, 2).toUpperCase() }}
                </span>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.name }}</div>
                <div class="text-caption text-medium-emphasis">{{ item.code }}</div>
              </div>
            </div>
          </template>

          <template #[`item.extendedName`]="{ item }">
            <div class="text-body-2">
              {{ item.extendedName || 'N/A' }}
            </div>
          </template>

          <template #[`item.description`]="{ item }">
            <div class="text-body-2">
              {{ truncateText(item.description, 40) }}
            </div>
          </template>

          <template #[`item.typeUse`]="{ item }">
            <v-chip :color="getTypeUseColor(item.typeUse)" variant="outlined" small>
              {{ item.typeUse || 'Sin tipo' }}
            </v-chip>
          </template>

          <template #[`item.utm`]="{ item }">
            <span class="font-weight-medium text-caption">{{ item.utm || 'Sin UTM' }}</span>
          </template>

          <template #[`item.coordinates`]="{ item }">
            <div class="text-caption">
              <div v-if="item.lat !== 0 || item.lng !== 0">
                <span class="text-primary">{{ formatCoordinate(item.lat, 'lat') }}</span><br>
                <span class="text-secondary">{{ formatCoordinate(item.lng, 'lng') }}</span>
              </div>
              <span v-else class="text-grey">Sin coordenadas</span>
            </div>
          </template>

          <template #[`item.altitude`]="{ item }">
            <v-chip v-if="item.altitude !== 0" color="green" variant="outlined" small>
              {{ item.altitude }} m
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <template #[`item.companyId`]="{ item }">
            <v-chip color="purple" variant="outlined" small>
              {{ item.companyId }}
            </v-chip>
          </template>

          <template #[`item.parentLocationId`]="{ item }">
            <v-chip v-if="item.parentLocationId" color="orange" variant="outlined" small>
              {{ truncateId(item.parentLocationId) }}
            </v-chip>
            <span v-else class="text-grey">Raíz</span>
          </template>

          <template #[`item.metadata`]="{ item }">
            <v-icon
              v-if="item.metadata"
              small
              color="info"
              @click="showMetadata(item)"
            >
              mdi-information
            </v-icon>
            <span v-else class="text-grey">-</span>
          </template>

          <template #[`item.createdAt`]="{ item }">
            {{ formatDate(item.createdAt) }}
          </template>

          <template #[`item.createdBy`]="{ item }">
            <span class="font-weight-medium">{{ item.createdBy || 'Sistema' }}</span>
          </template>

          <template #[`item.actions`]="{ item }">
            <div class="d-flex gap-1">
              <v-btn
                variant="text"
                @click="btnSetActiveEditLocation(item)"
                icon="mdi-pencil-outline"
                v-tooltip="`Editar`"
              />
              <v-btn
                variant="text"
                @click="viewOnMap(item)"
                icon="mdi-map-marker"
                v-tooltip="`Ver en mapa`"
              />
              <v-btn
                variant="text"
                @click="btnDeleteLocation(item)"
                icon="mdi-delete-outline"
                color="error"
                v-tooltip="`Eliminar`"
              />
            </div>
          </template>

          <template v-slot:bottom>
            <div class="text-center pt-2">
              <v-pagination v-model="page" :length="pageCount" />
            </div>
          </template>
        </v-data-table>
      </v-card>
    </div>

    <!-- Formulario de crear/editar ubicación -->
    <v-row v-if="activeCreateNewLocation || activeEditLocation" justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="4" class="bg-background pa-0 ma-0 rounded-lg content-container">
          <!-- Sticky Action Bar -->
          <div class="sticky-header bg-grey-lighten-4 d-flex justify-space-between align-center px-4 py-3"
               style="border-bottom: 1px solid #e0e0e0;">
            <span class="text-h6 font-weight-medium">
              {{ activeCreateNewLocation ? 'Crear nueva ubicación' : 'Editar ubicación' }}
            </span>
            <div>
              <v-btn
                color="red"
                variant="tonal"
                class="mr-2"
                rounded="lg"
                @click="resetForm"
              >
                Limpiar
              </v-btn>
              <v-btn
                color="primary"
                variant="tonal"
                class="mr-2"
                rounded="lg"
                @click="activeCreateNewLocation = false, activeEditLocation = false"
              >
                Volver
              </v-btn>
              <v-btn
                color="primary"
                class="text-white"
                rounded="lg"
                :loading="loadingCreateLocation || loadingEditLocation"
                @click="activeCreateNewLocation ? btnCreateLocation() : btnUpdateLocation()"
              >
                {{ activeCreateNewLocation ? 'Crear' : 'Actualizar' }}
              </v-btn>
            </div>
          </div>

          <!-- Main Content -->
          <v-row justify="center">
            <v-col cols="12">
              <v-sheet color="white" class="pa-6" elevation="0">
                <v-form ref="formRef" v-model="formValid" validate-on="submit lazy">
                  <div class="pa-6">
                    <h4 class="text-h6 mb-6">Información de la ubicación</h4>

                    <v-row>
                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model="location.code"
                          label="Código *"
                          :error-messages="formTouched && codeError ? [codeError] : []"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Código único de la ubicación"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model="location.name"
                          label="Nombre *"
                          :error-messages="formTouched && nameError ? [nameError] : []"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Nombre de la ubicación"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" class="mb-3">
                        <v-text-field
                          v-model="location.extendedName"
                          label="Nombre extendido"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Nombre completo de la ubicación (opcional)"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" class="mb-3">
                        <v-textarea
                          v-model="location.description"
                          label="Descripción"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Descripción detallada de la ubicación"
                          persistent-hint
                          rows="3"
                        />
                      </v-col>

                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model="location.typeUse"
                          label="Tipo de uso"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Tipo de uso de la ubicación"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model="location.utm"
                          label="Coordenadas UTM"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Coordenadas UTM"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" md="4" class="mb-3">
                        <v-text-field
                          v-model.number="location.lat"
                          label="Latitud"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Latitud en grados decimales"
                          persistent-hint
                          type="number"
                          step="0.000001"
                        />
                      </v-col>

                      <v-col cols="12" md="4" class="mb-3">
                        <v-text-field
                          v-model.number="location.lng"
                          label="Longitud"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Longitud en grados decimales"
                          persistent-hint
                          type="number"
                          step="0.000001"
                        />
                      </v-col>

                      <v-col cols="12" md="4" class="mb-3">
                        <v-text-field
                          v-model.number="location.altitude"
                          label="Altitud (m)"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Altitud en metros"
                          persistent-hint
                          type="number"
                          step="0.01"
                        />
                      </v-col>

                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model.number="location.companyId"
                          label="ID Empresa"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="ID de la empresa asociada"
                          persistent-hint
                          type="number"
                        />
                      </v-col>

                      <v-col cols="12" md="6" class="mb-3">
                        <v-text-field
                          v-model="location.parentLocationId"
                          label="ID Ubicación Padre"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="ID de la ubicación padre (opcional)"
                          persistent-hint
                        />
                      </v-col>

                      <v-col cols="12" class="mb-3">
                        <v-textarea
                          v-model="location.metadata"
                          label="Metadatos"
                          variant="outlined"
                          density="comfortable"
                          bg-color="white"
                          hint="Metadatos adicionales en formato JSON"
                          persistent-hint
                          rows="3"
                        />
                      </v-col>
                    </v-row>

                    <div class="text-caption text-medium-emphasis mt-4">
                      * Campos obligatorios
                    </div>
                  </div>
                </v-form>
              </v-sheet>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- Diálogo para mostrar metadatos -->
    <v-dialog v-model="metadataDialog" max-width="600px">
      <v-card>
        <v-card-title>
          <span class="text-h5">Metadatos de Ubicación</span>
        </v-card-title>
        <v-card-text>
          <pre class="metadata-content">{{ selectedMetadata }}</pre>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="metadataDialog = false">
            Cerrar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useLocationsStore } from './store'
import { Locations } from './models/locations'

const locationsStore = useLocationsStore()
const loading = ref(false)
const metadataDialog = ref(false)
const selectedMetadata = ref('')
const activeEditLocation = ref(false)
const activeCreateNewLocation = ref(false)
const loadingCreateLocation = ref(false)
const loadingEditLocation = ref(false)
const searchLocations = ref('')
const page = ref(1)
const itemsPerPage = ref(10)
const formTouched = ref(false)
const formRef = ref()
const formValid = ref(false)

const headers = [
  { title: 'CÓDIGO', key: 'code' },
  { title: 'NOMBRE', key: 'name' },
  { title: 'NOMBRE EXTENDIDO', key: 'extendedName' },
  { title: 'DESCRIPCIÓN', key: 'description' },
  { title: 'TIPO DE USO', key: 'typeUse' },
  { title: 'UTM', key: 'utm' },
  { title: 'COORDENADAS', key: 'coordinates' },
  { title: 'ALTITUD', key: 'altitude' },
  { title: 'ID EMPRESA', key: 'companyId' },
  { title: 'UBICACIÓN PADRE', key: 'parentLocationId' },
  { title: 'METADATOS', key: 'metadata' },
  { title: 'CREADO EN', key: 'createdAt' },
  { title: 'CREADO POR', key: 'createdBy' },
  { title: 'ACCIONES', key: 'actions' },
]

const locationsList = computed(() => locationsStore.locations)

// ─── REFS: Location ──────────────────────────────────────────────────────────
const locations = ref<Locations[]>([])
const location = ref<Locations>(new Locations())

// ─── ALERTAS ───────────────────────────────────────────────────────────────
const activeAlert = ref(false)
const textAlert = ref('')
const colorAlert = ref<'error' | 'success' | 'info' | 'warning'>('error')

// ─── VALIDACIONES ──────────────────────────────────────────────────────────
const codeError = ref('')
const nameError = ref('')

// ─── COMPUTED ──────────────────────────────────────────────────────────────
const pageCount = computed(() => Math.ceil(locationsList.value.length / itemsPerPage.value))

// ─── FUNCIONES AUXILIARES ──────────────────────────────────────────────────
const resetForm = () => {
  location.value = new Locations()
  codeError.value = ''
  nameError.value = ''
  formTouched.value = false
}

const formatDate = (date: Date | string) => {
  return new Date(date).toLocaleDateString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const truncateText = (text: string, maxLength: number) => {
  if (!text) return 'Sin información'
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text
}

const truncateId = (id: string) => {
  if (!id) return 'Sin ID'
  return id.length > 8 ? id.substring(0, 8) + '...' : id
}

const formatCoordinate = (value: number, type: 'lat' | 'lng') => {
  if (value === 0) return '-'
  const direction = type === 'lat' ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W')
  return `${Math.abs(value).toFixed(6)}° ${direction}`
}

const getTypeUseColor = (typeUse: string) => {
  switch (typeUse?.toLowerCase()) {
    case 'industrial':
      return 'blue'
    case 'comercial':
      return 'green'
    case 'residencial':
      return 'orange'
    case 'agricola':
      return 'brown'
    case 'minero':
      return 'purple'
    case 'forestal':
      return 'teal'
    default:
      return 'grey'
  }
}

const showMetadata = (item: Locations) => {
  selectedMetadata.value = item.metadata || 'Sin metadatos disponibles'
  metadataDialog.value = true
}

const viewOnMap = (item: Locations) => {
  if (item.lat !== 0 && item.lng !== 0) {
    // Abrir en Google Maps
    const url = `https://www.google.com/maps?q=${item.lat},${item.lng}`
    window.open(url, '_blank')
  } else {
    console.log('La ubicación no tiene coordenadas válidas')
  }
}

// ─── EVENTOS / BOTONES ─────────────────────────────────────────────────────
const getLocations = async () => {
  loading.value = true
  try {
    await locationsStore.getLocations()
    locations.value = locationsStore.locations
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al obtener las ubicaciones'
    colorAlert.value = 'error'
  }
  loading.value = false
}

const btnSetActiveCreateLocation = () => {
  activeCreateNewLocation.value = !activeCreateNewLocation.value
  location.value = new Locations()
  codeError.value = ''
  nameError.value = ''
}

const validateForm = (): boolean => {
  let isValid = true

  // Validación de campos obligatorios
  if (!location.value.code) {
    codeError.value = 'Código requerido'
    isValid = false
  } else {
    codeError.value = ''
  }

  if (!location.value.name) {
    nameError.value = 'Nombre requerido'
    isValid = false
  } else {
    nameError.value = ''
  }

  if (!isValid) {
    activeAlert.value = true
    textAlert.value = 'Todos los campos obligatorios deben ser completados'
    colorAlert.value = 'error'
  }

  return isValid
}

const btnSetActiveEditLocation = async (selectedLocation: Locations) => {
  location.value = new Locations(selectedLocation)
  activeEditLocation.value = true
}

const btnCreateLocation = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingCreateLocation.value = true

  try {
    await locationsStore.postLocations(location.value)
    activeAlert.value = true
    textAlert.value = 'Ubicación creada exitosamente'
    colorAlert.value = 'success'
    activeCreateNewLocation.value = false
    getLocations()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al crear la ubicación'
    colorAlert.value = 'error'
  }

  loadingCreateLocation.value = false
}

const btnUpdateLocation = async () => {
  formTouched.value = true

  const valid = await formRef.value?.validate()
  if (!valid.valid || !validateForm()) return

  loadingEditLocation.value = true

  try {
    await locationsStore.putLocations(location.value)
    activeAlert.value = true
    textAlert.value = 'Ubicación actualizada exitosamente'
    colorAlert.value = 'success'
    activeEditLocation.value = false
    getLocations()
  } catch (error) {
    activeAlert.value = true
    textAlert.value = 'Error al actualizar la ubicación'
    colorAlert.value = 'error'
  }

  loadingEditLocation.value = false
}

const btnDeleteLocation = async (locationToDelete: Locations) => {
  if (confirm(`¿Está seguro de eliminar la ubicación "${locationToDelete.name}"?`)) {
    try {
      await locationsStore.deleteLocations(locationToDelete.id)
      activeAlert.value = true
      textAlert.value = 'Ubicación eliminada exitosamente'
      colorAlert.value = 'success'
      getLocations()
    } catch (error) {
      activeAlert.value = true
      textAlert.value = 'Error al eliminar la ubicación'
      colorAlert.value = 'error'
    }
  }
}

// ─── LIFECYCLE ─────────────────────────────────────────────────────────────
onMounted(async () => {
  // Cargar ubicaciones desde el store
  await getLocations()
})
</script>

<style scoped>
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
}

.content-container {
  max-height: 80vh;
  overflow-y: auto;
}

.metadata-content {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  white-space: pre-wrap;
  word-wrap: break-word;
  max-height: 300px;
  overflow-y: auto;
}
</style>
